import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'
import type { Company } from 'types'

type UseCompanyParams = {
  id: string
}

type UseCompanyResult = {
  data: Company | null
  isLoading: boolean
  isError: boolean
  error: Error | null
  refetch: () => void
}

export const useCompanyQuery = (params: UseCompanyParams, options?: { enabled?: boolean }): UseCompanyResult => {
  const { id } = params
  const isEnabled = options?.enabled !== false && !!id

  const query = useQuery({
    queryKey: ['companies', 'detail', id],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: `/companies/admin/${id}`,
      })
      return response.data as Company
    },
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 10, // 10 minutes
  })

  return {
    data: query.data || null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

export const useCurrentUserCompany = (options?: { enabled?: boolean }): UseCompanyResult => {
  const isEnabled = options?.enabled !== false

  const query = useQuery({
    queryKey: ['companies', 'current'],
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/companies/admin/me',
      })
      return response.data as Company
    },
    enabled: isEnabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 10, // 10 minutes
  })

  return {
    data: query.data || null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

// Alias for backward compatibility
export const useCompany = useCompanyQuery
